import ErrorState from '@rio-cloud/rio-uikit/ErrorState';
import Spinner from '@rio-cloud/rio-uikit/Spinner';
import { FunctionComponent, useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { isUserSessionExpired } from '../../../configuration/login/loginSlice';
import { useAppDispatch, useAppSelector } from '../../../configuration/setup/hooks';
import { useGetTransportAssignmentsQuery } from '../api/transportOrderApi';
import { handleQueryError } from '../notifications/ErrorNotification';
import {
    getDebouncedTransportAssignmentSearchTerm,
    getFilters,
    getSelectedCategory,
    getSelectedTransportAssignment,
    getSortingForCategory,
    getTransportAssignmentSearchMode,
    transportAssignmentsSlice,
} from '../reducers/transportAssignmentsSlice';
import { LoadMoreButton } from '../sharedComponents/table/LoadMoreButton';
import { TableColumnSettingsState } from '../sharedComponents/table/TableColumnSettingsState';
import { useSetUserTriggeredFetching } from '../sharedComponents/userTriggeredFetchingHook';
import {
    transportAssignmentColumnDefinition,
    TransportAssignmentTableColumn,
} from './table/TransportAssignmentTableColumns';
import { TransportAssignmentsCardsSorting } from './table/TransportAssignmentsCardsSorting';
import { TransportAssignmentsTable } from './table/TransportAssignmentsTable';
import { TableCategory } from './tableCategory.types';
import { TransportAssignmentsTableToolbar } from './table/toolbar/TransportAssignmentsTableToolbar';
import { mapFilterToQueryParam, mapSearchValueToQueryParam } from './filter.types';

export const DEFAULT_TRANSPORT_ASSIGNMENTS_LIMIT_SIZE = 100;
export const MAX_TRANSPORT_ASSIGNMENTS_LIMIT_SIZE = 1000;

export const TransportAssignments: FunctionComponent = () => {
    const category = useAppSelector(getSelectedCategory);
    const selectedTransportAssignment = useAppSelector(getSelectedTransportAssignment);
    const dispatch = useAppDispatch();
    const [limit, setLimit] = useState(DEFAULT_TRANSPORT_ASSIGNMENTS_LIMIT_SIZE);
    const nextLimit = limit + DEFAULT_TRANSPORT_ASSIGNMENTS_LIMIT_SIZE;

    const { data: transportAssignmentsPage, isLoading, error } = useGetTransportAssignments(limit);

    useEffect(() => {
        if (error !== undefined) {
            handleQueryError(error);
        }
    }, [error]);

    if (isLoading) {
        return (
            <div data-testid="spinner">
                <Spinner />
            </div>
        );
    }

    if (transportAssignmentsPage === undefined) {
        return (
            <ErrorState
                headline={<FormattedMessage id="intl-msg:common-message.error.generic.headline" />}
                message={<FormattedMessage id="intl-msg:common-message.error.generic.message" />}
            />
        );
    }

    const transportAssignments = transportAssignmentsPage.items;

    if (selectedTransportAssignment !== undefined) {
        if (!transportAssignments.find((it) => it.id === selectedTransportAssignment.id)) {
            dispatch(transportAssignmentsSlice.actions.deselectTransportAssignment());
        }
    }

    const isArchiveTab = category === TableCategory.ARCHIVE;
    const tableId = `transportOrder.${category}`;
    const columnDefinition = getColumnDefinition(isArchiveTab);

    return (
        <TableColumnSettingsState key={tableId} tableId={tableId} columnDefinition={columnDefinition}>
            <TransportAssignmentsTableToolbar assignmentsPerStatus={transportAssignmentsPage.statusCount} />
            <TransportAssignmentsCardsSorting category={category} columnDefinition={columnDefinition} />
            <TransportAssignmentsTable transportAssignments={transportAssignments} />
            {transportAssignments.length > 0 && (
                <LoadMoreButton
                    onLoadMore={() => setLimit(nextLimit)}
                    hasMoreToLoad={transportAssignmentsPage.hasMore}
                    limitReached={nextLimit > MAX_TRANSPORT_ASSIGNMENTS_LIMIT_SIZE}
                />
            )}
        </TableColumnSettingsState>
    );
};

const getColumnDefinition = (isArchiveTab: boolean) => {
    return transportAssignmentColumnDefinition.filter(
        (def) => !isArchiveTab || def.column !== TransportAssignmentTableColumn.ACTION,
    );
};

const useGetTransportAssignments = (limit: number) => {
    const isSessionExpired = useAppSelector(isUserSessionExpired);
    const filters = useAppSelector(getFilters);
    const category = filters.category;
    const sorting = useAppSelector(getSortingForCategory(category));
    const searchMode = useAppSelector(getTransportAssignmentSearchMode);
    const debouncedSearchValue = useAppSelector(getDebouncedTransportAssignmentSearchTerm);

    const query = useGetTransportAssignmentsQuery(
        {
            ...mapFilterToQueryParam(filters),
            ...mapSearchValueToQueryParam(searchMode, debouncedSearchValue),
            sortBy: sorting,
            limit,
        },
        {
            skip: false,
            pollingInterval: isSessionExpired ? undefined : 60000,
        },
    );

    useSetUserTriggeredFetching({
        isFetching: query.isFetching,
        depsWhichTriggerUserFetching: [category, sorting, searchMode, debouncedSearchValue, filters],
    });

    return query;
};
